import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/i18n/LanguageContext";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://safranbolu-booking.emergent.host";

const HERO_IMAGE =
  "https://imgkit.otelz.com/turkiye/karabuk/safranbolu/fatma-hanim-konai_e6e121b6.jpg?tr=w-1200,q-85";

const SEO_STRINGS = {
  home: {
    tr: {
      title: "Fatma Hanım Konağı | Safranbolu UNESCO Butik Otel — Rezervasyon",
      description: "Safranbolu Çeşme Mahallesi'nde 110 yıllık tarihi konak. 11 oda, açık büfe yöresel kahvaltı, ahşap tavanlı odalar. Online rezervasyon ve WhatsApp ile hızlı iletişim. +90 378 228 81 00",
      keywords: "fatma hanım konağı, safranbolu otel, safranbolu konak otel, safranbolu butik otel, safranbolu rezervasyon, çarşı mahallesi otel, unesco safranbolu, safranbolu konaklama, safranbolu osmanlı evi",
    },
    en: {
      title: "Fatma Hanım Konağı | Safranbolu UNESCO Boutique Hotel — Book Now",
      description: "A 110-year-old historic mansion in Safranbolu (UNESCO World Heritage). 11 rooms, open-buffet local breakfast, wooden-ceiling rooms. Online booking & WhatsApp. +90 378 228 81 00",
      keywords: "fatma hanim konagi, safranbolu hotel, safranbolu boutique hotel, ottoman mansion hotel, unesco safranbolu, safranbolu booking, safranbolu accommodation, turkey heritage hotel",
    },
    ar: {
      title: "قصر فاطمة هانم | فندق بوتيك صفرانبولو التراثي — احجز الآن",
      description: "قصر تاريخي عمره 110 عامًا في صفرانبولو (تراث اليونسكو العالمي). 11 غرفة، إفطار محلي مفتوح، أسقف خشبية. حجز عبر الإنترنت وواتساب. +90 378 228 81 00",
      keywords: "قصر فاطمة هانم, فندق صفرانبولو, صفرانبولو بوتيك, قصر عثماني, تراث اليونسكو, حجز صفرانبولو, إقامة في صفرانبولو, فندق تراثي تركيا",
    },
  },
  booking: {
    tr: {
      title: "Rezervasyon — Fatma Hanım Konağı Safranbolu",
      description: "Fatma Hanım Konağı'nda online rezervasyon talebi: ad, tarih, kişi sayısı ve oda tipinizi seçin — kısa süre içinde sizi telefonla arayalım.",
      keywords: "safranbolu rezervasyon, fatma hanım konağı rezervasyon, safranbolu otel ayırtma, online booking safranbolu",
    },
    en: {
      title: "Reservation — Fatma Hanım Konağı Safranbolu",
      description: "Submit a reservation request at Fatma Hanım Konağı: name, dates, guests, room type — we'll call you to confirm.",
      keywords: "safranbolu reservation, fatma hanim konagi booking, safranbolu hotel reservation, online booking safranbolu",
    },
    ar: {
      title: "الحجز — قصر فاطمة هانم صفرانبولو",
      description: "أرسل طلب حجز في قصر فاطمة هانم: الاسم، التواريخ، عدد الضيوف، نوع الغرفة — سنتصل بك للتأكيد.",
      keywords: "حجز صفرانبولو, قصر فاطمة هانم, حجز فندق صفرانبولو, الحجز عبر الإنترنت صفرانبولو",
    },
  },
};

const BASE_HOTEL_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "@id": SITE_URL + "/#hotel",
  "name": "Fatma Hanım Konağı",
  "alternateName": ["Fatma Hanim Konagi", "Fatma Hanim Konagi Butik Otel"],
  "description": "Historic Ottoman mansion boutique hotel in the UNESCO-listed old town of Safranbolu, Turkey.",
  "url": SITE_URL + "/",
  "image": [HERO_IMAGE],
  "telephone": "+90-378-228-81-00",
  "priceRange": "₺2800 - ₺4500",
  "currenciesAccepted": "TRY",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Çeşme Mahallesi, Eskicami Cami Sokak No:12",
    "addressLocality": "Safranbolu",
    "addressRegion": "Karabük",
    "postalCode": "78600",
    "addressCountry": "TR",
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 41.2543,
    "longitude": 32.6950,
  },
  "starRating": { "@type": "Rating", "ratingValue": "4" },
  "checkinTime": "14:00",
  "checkoutTime": "11:00",
  "petsAllowed": false,
  "smokingAllowed": false,
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Free Wi-Fi", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Open buffet breakfast", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Stone fireplace rooms", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Family rooms", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "24h reception", "value": true },
  ],
};

const SEO = ({
  page = "home",
  rooms = [],
  testimonials = [],
}) => {
  const { lang } = useLanguage();
  const s = (SEO_STRINGS[page] || SEO_STRINGS.home)[lang] || SEO_STRINGS.home.tr;
  const url = SITE_URL + (page === "booking" ? "/booking" : "/");

  // Build dynamic JSON-LD with rooms as Accommodation offers
  const hotel = { ...BASE_HOTEL_JSONLD };
  if (page === "home" && rooms.length) {
    hotel.makesOffer = rooms.map((r) => ({
      "@type": "Offer",
      "name": r.name?.[lang] || r.name?.tr,
      "url": `${SITE_URL}/booking?room=${r.id}`,
      "priceCurrency": "TRY",
      "price": r.price_try,
      "availability": "https://schema.org/InStock",
      "itemOffered": {
        "@type": "Accommodation",
        "name": r.name?.[lang] || r.name?.tr,
        "description": r.description?.[lang] || r.description?.tr,
        "occupancy": { "@type": "QuantitativeValue", "maxValue": r.capacity },
        "floorSize": { "@type": "QuantitativeValue", "value": r.size_m2, "unitCode": "MTK" },
      },
    }));
  }
  if (page === "home" && testimonials.length) {
    hotel.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": (
        testimonials.reduce((s, t) => s + (t.rating || 5), 0) / testimonials.length
      ).toFixed(1),
      "reviewCount": testimonials.length,
      "bestRating": "5",
      "worstRating": "1",
    };
    hotel.review = testimonials.slice(0, 3).map((t) => ({
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": t.rating || 5, "bestRating": "5" },
      "author": { "@type": "Person", "name": t.name?.[lang] || t.name?.tr || "Guest" },
      "reviewBody": t.text?.[lang] || t.text?.tr || "",
    }));
  }

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Fatma Hanım Konağı", "item": SITE_URL + "/" },
      ...(page === "booking"
        ? [{ "@type": "ListItem", "position": 2, "name": "Reservation", "item": SITE_URL + "/booking" }]
        : []),
    ],
  };

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{s.title}</title>
      <meta name="description" content={s.description} />
      <meta name="keywords" content={s.keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="author" content="Fatma Hanım Konağı" />
      <meta name="theme-color" content="#1E3B2E" />
      <link rel="canonical" href={url} />

      {/* hreflang */}
      <link rel="alternate" hrefLang="tr" href={SITE_URL + (page === "booking" ? "/booking" : "/")} />
      <link rel="alternate" hrefLang="en" href={SITE_URL + (page === "booking" ? "/booking?lang=en" : "/?lang=en")} />
      <link rel="alternate" hrefLang="ar" href={SITE_URL + (page === "booking" ? "/booking?lang=ar" : "/?lang=ar")} />
      <link rel="alternate" hrefLang="x-default" href={SITE_URL + (page === "booking" ? "/booking" : "/")} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fatma Hanım Konağı" />
      <meta property="og:title" content={s.title} />
      <meta property="og:description" content={s.description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang === "tr" ? "tr_TR" : lang === "ar" ? "ar_AR" : "en_US"} />
      <meta property="og:image" content={HERO_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="800" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={s.title} />
      <meta name="twitter:description" content={s.description} />
      <meta name="twitter:image" content={HERO_IMAGE} />

      {/* Geo */}
      <meta name="geo.region" content="TR-78" />
      <meta name="geo.placename" content="Safranbolu" />
      <meta name="geo.position" content="41.2543;32.6950" />
      <meta name="ICBM" content="41.2543, 32.6950" />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(hotel)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
  );
};

export default SEO;
